import React, { useState, useEffect } from 'react';
import { UserPlus, CheckCircle, CreditCard, X, Loader2 } from 'lucide-react';
import { useSocket } from '../context/SocketContext';
import api from '../utils/api';

interface BankDetails {
  accountHolderName: string;
  bankName: string;
  accountNumber: string;
  branch: string;
}

interface Driver {
  id: string;
  name: string;
  phone: string;
  email?: string;
  isVerified?: boolean;
  bankDetails?: BankDetails;
  registrationDate?: string;
}

const NewDrivers = () => {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [bankForm, setBankForm] = useState<BankDetails>({
    accountHolderName: '',
    bankName: '',
    accountNumber: '',
    branch: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { socket } = useSocket();

  const fetchDrivers = async () => {
    try {
      const response = await api.get('/drivers');
      const data = response.data;
      console.log("NewDrivers: Received data from server:", data);

      if (Array.isArray(data)) {
        // Filter for drivers who are not yet verified
        const pendingDrivers = data.filter((d: Driver) => !d.isVerified);
        console.log("Filtered pending drivers:", pendingDrivers);
        setDrivers(pendingDrivers);
      } else {
        console.error("Server did not return an array of drivers:", data);
        setDrivers([]);
      }
    } catch (error) {
      console.error('Error fetching drivers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();

    if (socket) {
      socket.on('driver_updated', (updatedData: { id: string, bankDetails: BankDetails }) => {
        setDrivers(prevDrivers => 
          prevDrivers.map(d => d.id === updatedData.id ? { ...d, bankDetails: updatedData.bankDetails } : d)
        );
      });

      socket.on('driver_approved', (data: { id: string }) => {
        setDrivers(prevDrivers => prevDrivers.filter(d => d.id !== data.id));
      });
      
      // Also listen for new registrations if you add that later
      socket.on('new_driver_registered', () => {
        fetchDrivers();
      });
    }

    return () => {
      if (socket) {
        socket.off('driver_updated');
        socket.off('driver_approved');
        socket.off('new_driver_registered');
      }
    };
  }, [socket]);

  const handleAddBankClick = (driver: Driver) => {
    setSelectedDriver(driver);
    setBankForm(driver.bankDetails || {
      accountHolderName: '',
      bankName: '',
      accountNumber: '',
      branch: ''
    });
    setIsModalOpen(true);
  };

  const handleBankSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDriver) return;

    setIsSubmitting(true);
    try {
      await api.patch(`/driver/${selectedDriver.id}/bank-details`, { bankDetails: bankForm });
      setIsModalOpen(false);
      fetchDrivers(); // Refresh list to show updated data
    } catch (error) {
      console.error('Error saving bank details:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApprove = async (driverId: string) => {
    try {
      await api.patch(`/driver/${driverId}/approve`);
      // Remove from local state
      setDrivers(drivers.filter(d => d.id !== driverId));
    } catch (error) {
      console.error('Error approving driver:', error);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-[2rem] p-12 flex flex-col items-center justify-center border border-gray-100 shadow-sm min-h-[400px]">
        <Loader2 className="w-10 h-10 text-[#05A664] animate-spin mb-4" />
        <p className="text-[#909090] font-medium">Loading new drivers...</p>
      </div>
    );
  }

  const validateForm = () => {
    const charsOnly = /^[a-zA-Z\s]*$/;
    const numsOnly = /^[0-9]*$/;

    const isHolderValid = bankForm.accountHolderName.length > 0 && charsOnly.test(bankForm.accountHolderName);
    const isBankValid = bankForm.bankName.length > 0 && charsOnly.test(bankForm.bankName);
    const isAccValid = bankForm.accountNumber.length > 0 && numsOnly.test(bankForm.accountNumber);
    const isBranchValid = bankForm.branch.length > 0 && charsOnly.test(bankForm.branch);

    return isHolderValid && isBankValid && isAccValid && isBranchValid;
  };

  const isFormValid = validateForm();

  return (
    <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-hidden">
      <div className="p-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#E8F5EE] rounded-xl flex items-center justify-center">
            <UserPlus className="text-[#05A664] w-5 h-5" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#121415]">New Registered Drivers</h2>
            <p className="text-sm text-[#909090] font-medium">Action required to verify accounts</p>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-[#F1F8F5]">
              <th className="px-8 py-4 text-xs font-bold text-[#909090] uppercase tracking-widest border-b border-gray-100">Driver Name</th>
              <th className="px-8 py-4 text-xs font-bold text-[#909090] uppercase tracking-widest border-b border-gray-100">Contact Info</th>
              <th className="px-8 py-4 text-xs font-bold text-[#909090] uppercase tracking-widest border-b border-gray-100">Bank Details</th>
              <th className="px-8 py-4 text-xs font-bold text-[#909090] uppercase tracking-widest border-b border-gray-100 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {drivers.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-8 py-12 text-center text-[#909090] font-medium">
                  No new drivers pending verification.
                </td>
              </tr>
            ) : (
              drivers.map((driver) => (
                <tr key={driver.id} className="hover:bg-[#F1F8F5]/30 transition-colors group">
                  <td className="px-8 py-6">
                    <div className="font-bold text-[#121415]">{driver.name}</div>
                    <div className="text-xs text-[#909090] mt-1">ID: {driver.id.substring(0, 8)}...</div>
                  </td>
                  <td className="px-8 py-6">
                    <div className="text-sm font-medium text-[#121415]">{driver.phone}</div>
                    <div className="text-xs text-[#909090]">{driver.email || 'No email provided'}</div>
                  </td>
                  <td className="px-8 py-6">
                    {driver.bankDetails ? (
                      <div className="flex flex-col gap-0.5">
                        <span className="text-sm font-bold text-[#05A664] flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Info Added
                        </span>
                        <span className="text-xs text-[#909090]">{driver.bankDetails.bankName} - {driver.bankDetails.accountNumber}</span>
                      </div>
                    ) : (
                      <span className="text-xs font-bold text-orange-500 bg-orange-50 px-3 py-1 rounded-full">
                        Needs Info
                      </span>
                    )}
                  </td>
                  <td className="px-8 py-6 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        onClick={() => handleAddBankClick(driver)}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold border border-gray-200 text-[#121415] hover:bg-gray-50 transition-all"
                      >
                        <CreditCard className="w-4 h-4" />
                        {driver.bankDetails ? 'Edit Bank' : 'Add Bank'}
                      </button>
                      <button
                        disabled={!driver.bankDetails}
                        onClick={() => handleApprove(driver.id)}
                        className={`flex items-center gap-2 px-6 py-2 rounded-xl text-sm font-bold transition-all shadow-sm ${driver.bankDetails
                          ? 'bg-[#05A664] text-white hover:bg-[#048b53] hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                      >
                        Processed
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Bank Details Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#121415]/40 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white rounded-[2.5rem] w-full max-w-md overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">
            <div className="px-8 pt-8 pb-4 border-b border-gray-50 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-[#05A664]">Bank Account Details</h3>
                <p className="text-sm text-[#909090] font-medium">Enter manual bank info for {selectedDriver?.name}</p>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-[#909090]" />
              </button>
            </div>

            <form onSubmit={handleBankSubmit} className="px-8 pb-8 pt-4 space-y-5">
              <div className="space-y-2">
                <label className="text-xs font-bold text-[#000000] uppercase tracking-wider">Account Holder's Name</label>
                <input
                  required
                  type="text"
                  placeholder="Type Account Holder's Name"
                  className={`w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl text-sm font-medium focus:ring-2 outline-none transition-all placeholder:text-gray-300 ${bankForm.accountHolderName && !/^[a-zA-Z\s]*$/.test(bankForm.accountHolderName)
                    ? 'ring-2 ring-red-500/50'
                    : 'focus:ring-[#05A664]/20'
                    }`}
                  value={bankForm.accountHolderName}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^[a-zA-Z\s]*$/.test(val)) {
                      setBankForm({ ...bankForm, accountHolderName: val });
                    }
                  }}
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-[#000000] uppercase tracking-wider">Bank Name</label>
                <input
                  required
                  type="text"
                  placeholder="Type Bank Name"
                  className={`w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl text-sm font-medium focus:ring-2 outline-none transition-all placeholder:text-gray-300 ${bankForm.bankName && !/^[a-zA-Z\s]*$/.test(bankForm.bankName)
                    ? 'ring-2 ring-red-500/50'
                    : 'focus:ring-[#05A664]/20'
                    }`}
                  value={bankForm.bankName}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^[a-zA-Z\s]*$/.test(val)) {
                      setBankForm({ ...bankForm, bankName: val });
                    }
                  }}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-[#000000] uppercase tracking-wider">Account Number</label>
                  <input
                    required
                    type="text"
                    placeholder="Type Account Number"
                    className={`w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl text-sm font-medium focus:ring-2 outline-none transition-all placeholder:text-gray-300 ${bankForm.accountNumber && !/^[0-9]*$/.test(bankForm.accountNumber)
                      ? 'ring-2 ring-red-500/50'
                      : 'focus:ring-[#05A664]/20'
                      }`}
                    value={bankForm.accountNumber}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^[0-9]*$/.test(val)) {
                        setBankForm({ ...bankForm, accountNumber: val });
                      }
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-[#000000] uppercase tracking-wider">Branch</label>
                  <input
                    required
                    type="text"
                    placeholder="Type Branch Name"
                    className={`w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl text-sm font-medium focus:ring-2 outline-none transition-all placeholder:text-gray-300 ${bankForm.branch && !/^[a-zA-Z\s]*$/.test(bankForm.branch)
                      ? 'ring-2 ring-red-500/50'
                      : 'focus:ring-[#05A664]/20'
                      }`}
                    value={bankForm.branch}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^[a-zA-Z\s]*$/.test(val)) {
                        setBankForm({ ...bankForm, branch: val });
                      }
                    }}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !isFormValid}
                className={`w-full py-4 rounded-2xl font-bold text-sm shadow-lg transition-all flex items-center justify-center gap-2 mt-4 ${isFormValid && !isSubmitting
                  ? 'bg-[#05A664] text-white hover:bg-black hover:shadow-xl'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-none'
                  }`}
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Save Bank Details'
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewDrivers;
